import { io, type Socket } from 'socket.io-client';

// The chat-api-guide instructs the client to mount on the same origin used
// for REST. The REST base URL ends in `/api/v1`; the Socket.IO path lives at
// the origin root, so we strip the trailing path segment before passing it
// to `io()`.
function deriveSocketUrl(): string {
  const apiBase = import.meta.env.VITE_API_URL || 'https://api.tereiko.com/api/v1';
  try {
    const u = new URL(apiBase);
    return `${u.protocol}//${u.host}`;
  } catch {
    // Relative or malformed — fall back to current origin.
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
}

type EventHandler = (...args: unknown[]) => void;

interface ChatSocketHandle {
  socket: Socket | null;
  token: string | null;
  // Rooms we want to be subscribed to. If a subscribe is requested while the
  // socket is connecting, we re-emit on connect.
  subscriptions: Set<string>;
}

const handle: ChatSocketHandle = {
  socket: null,
  token: null,
  subscriptions: new Set(),
};

export interface ConnectOptions {
  token: string;
  onConnectError?: (err: Error) => void;
}

export function connect(opts: ConnectOptions): Socket {
  // Same token + existing socket: return it whether it's connecting,
  // connected, or reconnecting. socket.io handles its own lifecycle, and
  // tearing it down here racily — for example under React 18 StrictMode's
  // double-invoke — produces "WebSocket is closed before the connection is
  // established" when the second invocation kills an in-flight handshake.
  if (handle.socket && handle.token === opts.token) {
    return handle.socket;
  }

  if (handle.socket) {
    // Token actually changed (re-login or refresh). Force a fresh handshake.
    handle.socket.removeAllListeners();
    handle.socket.disconnect();
  }

  handle.token = opts.token;
  const socket = io(deriveSocketUrl(), {
    transports: ['websocket'],
    auth: { token: opts.token },
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
    reconnectionAttempts: Infinity,
    withCredentials: true,
  });

  socket.on('connect', () => {
    // Re-emit any pending room subscriptions after reconnect.
    for (const roomId of handle.subscriptions) {
      socket.emit('chat:subscribe', { room_id: roomId });
    }
  });

  if (opts.onConnectError) {
    socket.on('connect_error', opts.onConnectError);
  }

  handle.socket = socket;
  return socket;
}

export function disconnect(): void {
  if (handle.socket) {
    handle.socket.removeAllListeners();
    handle.socket.disconnect();
    handle.socket = null;
    handle.token = null;
    handle.subscriptions.clear();
  }
}

export function getSocket(): Socket | null {
  return handle.socket;
}

export function subscribeRoom(roomId: string): void {
  handle.subscriptions.add(roomId);
  if (handle.socket?.connected) {
    handle.socket.emit('chat:subscribe', { room_id: roomId });
  }
}

export function unsubscribeRoom(roomId: string): void {
  handle.subscriptions.delete(roomId);
  if (handle.socket?.connected) {
    handle.socket.emit('chat:unsubscribe', { room_id: roomId });
  }
}

export function on(event: string, handler: EventHandler): () => void {
  handle.socket?.on(event, handler);
  return () => {
    handle.socket?.off(event, handler);
  };
}

// For tests: reset the singleton between specs.
export function _resetForTests(): void {
  disconnect();
}
